import { useTranslation } from "react-i18next";
import {
  Trash2,
  Check,
  Lock,
  Users,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Trophy,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Progress } from "@tabipla/ui";
import { CountdownBadge } from "../CountdownBadge";
import type { PollCardProps } from "./types";

/**
 * PollCard presenter component.
 * Pure presentation - receives all data and callbacks as props.
 * Does not contain any hooks for data fetching or mutations.
 */
export function PollCard({
  poll,
  isExpanded,
  maxVotes,
  totalOptionVotes,
  winnerIndex,
  isDisabled,
  isVoting,
  isClosing,
  isDeleting,
  onToggleExpand,
  onVote,
  onClose,
  onDelete,
}: PollCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardHeader
        className="cursor-pointer p-4 transition-colors hover:bg-muted/30"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base font-semibold">{poll.question}</CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {t("polls.votersCount", { count: poll.totalVotes })}
              </span>
              {poll.allowMultiple && (
                <Badge variant="secondary" className="text-xs">
                  {t("polls.multipleChoice")}
                </Badge>
              )}
              {poll.isExpired && !poll.isClosed && (
                <Badge variant="destructive" className="text-xs">
                  <AlertCircle className="mr-1 h-3 w-3" />
                  {t("polls.expired")}
                </Badge>
              )}
              {poll.deadline && !poll.isExpired && !poll.isClosed && (
                <CountdownBadge deadline={poll.deadline} variant="compact" />
              )}
              {poll.isClosed && (
                <Badge variant="outline" className="text-xs">
                  <Lock className="mr-1 h-3 w-3" />
                  {t("polls.closed")}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full bg-muted/50">
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          <div className="space-y-3 px-4 pb-4">
            {poll.results.map((result) => {
              const isSelected = poll.myVotes.includes(result.optionIndex);
              const barPercentage =
                poll.totalVotes > 0 ? Math.round((result.votes / maxVotes) * 100) : 0;
              const votePercentage =
                totalOptionVotes > 0 ? Math.round((result.votes / totalOptionVotes) * 100) : 0;
              const isWinner = winnerIndex === result.optionIndex && result.votes > 0;

              return (
                <button
                  key={result.optionIndex}
                  onClick={() => !isDisabled && onVote(result.optionIndex)}
                  disabled={isDisabled || isVoting}
                  className={`relative w-full overflow-hidden rounded-xl border p-4 text-left transition-all active:scale-[0.98] ${
                    isDisabled
                      ? "cursor-default opacity-80"
                      : "cursor-pointer hover:border-primary hover:shadow-md"
                  } ${
                    isWinner && isDisabled
                      ? "border-yellow-500 bg-yellow-50 shadow-sm dark:bg-yellow-950/20"
                      : isSelected
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-muted hover:bg-muted/30"
                  }`}
                >
                  {/* Progress bar background */}
                  <div
                    className={`absolute inset-0 transition-all ${
                      isWinner && isDisabled
                        ? "bg-yellow-200/50 dark:bg-yellow-800/30"
                        : isSelected
                          ? "bg-primary/10"
                          : "bg-muted/50"
                    }`}
                    style={{ width: `${barPercentage}%` }}
                  />

                  {/* Content */}
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isWinner && isDisabled ? (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-white">
                          <Trophy className="h-3 w-3" />
                        </div>
                      ) : (
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground/30"
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                      )}
                      <span className={isSelected || isWinner ? "font-medium" : ""}>
                        {result.option}
                      </span>
                      {isWinner && isDisabled && (
                        <Badge className="bg-yellow-500 text-xs text-white hover:bg-yellow-600">
                          {t("polls.winner")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {totalOptionVotes > 0 && (
                        <span className="text-sm font-medium text-muted-foreground">
                          {votePercentage}%
                        </span>
                      )}
                      <span className="text-sm text-muted-foreground">({result.votes})</span>
                    </div>
                  </div>

                  {/* Voters (if any) */}
                  {result.voters.length > 0 && (
                    <div className="relative mt-2 flex flex-wrap gap-1">
                      {result.voters.slice(0, 5).map((voter) => (
                        <Badge key={voter.userId} variant="secondary" className="text-xs">
                          {voter.userName}
                        </Badge>
                      ))}
                      {result.voters.length > 5 && (
                        <Badge variant="secondary" className="text-xs">
                          +{result.voters.length - 5}
                        </Badge>
                      )}
                    </div>
                  )}
                </button>
              );
            })}

            {/* Poll progress */}
            {poll.totalVotes > 0 && (
              <div className="mt-3 space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("polls.participation")}</span>
                  <span>
                    {poll.totalVotes} {t("polls.voters")}
                  </span>
                </div>
                <Progress value={100} className="h-1" />
              </div>
            )}

            {/* Actions */}
            {!isDisabled && (
              <div className="mt-4 flex gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  disabled={isClosing}
                  className="flex-1 rounded-xl"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  {t("polls.closePoll")}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="min-h-[44px] min-w-[44px] rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            )}

            {isDisabled && (
              <div className="mt-4 flex gap-2 border-t pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common.delete")}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
